import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const COOLDOWN_MS = 5 * 60 * 1000; // Re-check after 5 minutes

interface ServiceAvailabilityState {
  available: boolean;
  unavailableSince: number | null;
}

interface ServiceAvailabilityContextValue {
  isAvailable: (serviceName: string) => boolean;
  markUnavailable: (serviceName: string) => void;
}

const ServiceAvailabilityContext =
  createContext<ServiceAvailabilityContextValue | null>(null);

export const ServiceAvailabilityProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const servicesRef = useRef<Record<string, ServiceAvailabilityState>>({});
  const [version, setVersion] = useState(0);

  // Periodically re-enable services whose cooldown has expired
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      let changed = false;
      for (const [, state] of Object.entries(servicesRef.current)) {
        if (
          !state.available &&
          state.unavailableSince &&
          now - state.unavailableSince > COOLDOWN_MS
        ) {
          state.available = true;
          state.unavailableSince = null;
          changed = true;
        }
      }
      if (changed) {
        setVersion((n) => n + 1);
      }
    }, 60_000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const markUnavailable = useCallback((serviceName: string) => {
    const current = servicesRef.current[serviceName];
    if (!current || current.available) {
      servicesRef.current = {
        ...servicesRef.current,
        [serviceName]: { available: false, unavailableSince: Date.now() },
      };
      setVersion((n) => n + 1);
    }
  }, []);

  const isAvailable = useCallback((serviceName: string) => {
    const state = servicesRef.current[serviceName];
    if (!state) return true;
    return state.available;
  }, []);

  // Include version so consumers re-render when availability changes
  const value = useMemo(
    () => ({ isAvailable, markUnavailable }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAvailable, markUnavailable, version],
  );

  return (
    <ServiceAvailabilityContext.Provider value={value}>
      {children}
    </ServiceAvailabilityContext.Provider>
  );
};

export const useServiceAvailability = () => {
  const context = useContext(ServiceAvailabilityContext);
  if (!context) {
    // Return a no-op implementation when used outside the provider
    return {
      isAvailable: () => true,
      markUnavailable: () => {},
    };
  }
  return context;
};
